import { Clock3, GitCompare, MessagesSquare, Plus, RotateCcw } from 'lucide-react';
import * as React from 'react';
import Loading from '@/components/shared/Loading';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/NewButton';
import {
  draftVersionActivityLabel,
  formatHistoryDay,
  formatHistoryTime,
  groupDraftVersionsByDay,
} from '@/drafting/utils/history';
import type {
  DraftDocumentChatReference,
  DraftDocumentVersionHistoryItem,
} from '@/hooks/useDraftDocuments';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';

const versionActionClassName = (
  'inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 hover:underline'
  + ' disabled:pointer-events-none disabled:opacity-50 disabled:no-underline'
);

export function DraftHistoryPanel({
  canRecordVersion,
  createdFromChat,
  dateLocale,
  isLoading,
  isLiveSelected,
  isRecordingVersion,
  isRestoreDisabled,
  latestVersionNumber,
  liveEditedAt,
  onOpenSourceChat,
  onRecordVersion,
  onSelectLive,
  onSelectVersion,
  onShowDiff,
  onRestoreVersion,
  recordVersionDisabledReason,
  restoringVersionNumber,
  selectedVersionNumber,
  versions,
}: {
  canRecordVersion: boolean;
  createdFromChat?: DraftDocumentChatReference | null;
  dateLocale: RegionalDateLocale;
  isLoading: boolean;
  isLiveSelected: boolean;
  isRecordingVersion: boolean;
  isRestoreDisabled?: boolean;
  latestVersionNumber: number | null | undefined;
  liveEditedAt?: string | null;
  onOpenSourceChat?: () => void;
  onRecordVersion: () => void;
  onSelectLive: () => void;
  onSelectVersion: (versionNumber: number) => void;
  onShowDiff: (versionNumber: number) => void;
  onRestoreVersion: (versionNumber: number) => void;
  recordVersionDisabledReason?: string | null;
  restoringVersionNumber?: number | null;
  selectedVersionNumber: number;
  versions: DraftDocumentVersionHistoryItem[];
}) {
  const groups = React.useMemo(() => groupDraftVersionsByDay(versions, dateLocale), [dateLocale, versions]);
  const liveHistoryDate = liveEditedAt ?? new Date().toISOString();
  const liveGroupLabel = formatHistoryDay(liveHistoryDate, dateLocale);
  const firstGroup = groups[0] ?? null;
  const liveGroupVersions = firstGroup?.label === liveGroupLabel ? firstGroup.versions : [];
  const recordedGroups = firstGroup?.label === liveGroupLabel ? groups.slice(1) : groups;

  const renderVersionHistoryItem = (
    version: DraftDocumentVersionHistoryItem,
    index: number,
    groupVersions: DraftDocumentVersionHistoryItem[],
  ) => {
    const isSelected = !isLiveSelected && version.version_number === selectedVersionNumber;
    const isLatest = version.version_number === latestVersionNumber;
    const isLast = index === groupVersions.length - 1;
    const isRestoring = restoringVersionNumber === version.version_number;
    const activityLabel = draftVersionActivityLabel(version, dateLocale);
    const showSourceChat = version.version_number === 1 && Boolean(createdFromChat) && Boolean(onOpenSourceChat);
    const showChanges = version.version_number > 1;
    const showRestore = !isLatest;

    return (
      <li key={version.guid} className="relative flex gap-3 pb-4 last:pb-0">
        <span className="w-12 shrink-0 pt-0.5 text-right text-[11px] tabular-nums text-slate-400">
          {formatHistoryTime(version.created_at)}
        </span>
        <div className="relative flex w-3 shrink-0 flex-col items-center">
          <span
            className={cn(
              'mt-1.5 h-2 w-2 rounded-full',
              isSelected ? 'bg-amber-500' : 'bg-slate-300',
            )}
          />
          {!isLast && (
            <span className="absolute top-4 -bottom-[18px] left-1/2 w-px -translate-x-1/2 bg-slate-200" />
          )}
        </div>
        <div
          className={cn(
            '-m-2 min-w-0 flex-1 rounded-md p-2 text-left transition-colors hover:bg-slate-50/70',
          )}
        >
          <button
            type="button"
            className="min-w-0 w-full rounded-sm text-left focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            onClick={() => onSelectVersion(version.version_number)}
          >
            <p className="text-sm font-medium text-slate-900">
              Version {version.version_number}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">{activityLabel}</p>
          </button>
          {showSourceChat || showChanges || showRestore ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              {showSourceChat ? (
                <button
                  type="button"
                  title={createdFromChat?.title ?? undefined}
                  className={versionActionClassName}
                  onClick={onOpenSourceChat}
                >
                  <MessagesSquare className="h-3 w-3" />
                  View source chat
                </button>
              ) : null}
              {showChanges ? (
                <button
                  type="button"
                  className={versionActionClassName}
                  aria-label={`Show changes for version ${version.version_number}`}
                  onClick={() => onShowDiff(version.version_number)}
                >
                  <GitCompare className="h-3 w-3" />
                  Changes
                </button>
              ) : null}
              {showRestore ? (
                <button
                  type="button"
                  className={versionActionClassName}
                  aria-label={`Restore version ${version.version_number}`}
                  disabled={isRestoreDisabled || Boolean(restoringVersionNumber)}
                  onClick={() => onRestoreVersion(version.version_number)}
                >
                  {isRestoring ? <Spinner size="xs" /> : <RotateCcw className="h-3 w-3" />}
                  {isRestoring ? 'Restoring' : 'Restore'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="border-b border-slate-200 px-5 py-3">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 text-sm text-slate-500">Recorded formal versions of this draft.</p>
          <Button
            variant="outline"
            size="sm"
            icon={Plus}
            text="Create version"
            loading={isRecordingVersion}
            loadingText="Creating"
            disabled={!canRecordVersion}
            tooltip={recordVersionDisabledReason ?? 'Create a new version from the current draft.'}
            className="shrink-0 border-slate-200 bg-white text-slate-700 shadow-none hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 active:bg-slate-100"
            onClick={onRecordVersion}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loading />
          </div>
        ) : (
          <>
            <section className="mb-5">
              <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                <Clock3 className="h-3 w-3" />
                {liveGroupLabel}
              </div>
              <ol className="relative">
                <li className="relative flex gap-3 pb-4 last:pb-0">
                  <span className="w-12 shrink-0 pt-0.5 text-right text-[11px] tabular-nums text-slate-400">
                    <span className="sr-only">Live editing</span>
                  </span>
                  <div className="relative flex w-3 shrink-0 flex-col items-center">
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 rounded-full',
                        isLiveSelected ? 'bg-teal-500' : 'bg-slate-300',
                      )}
                    />
                    {liveGroupVersions.length ? (
                      <span className="absolute top-4 -bottom-[18px] left-1/2 w-px -translate-x-1/2 bg-slate-200" />
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={cn(
                      '-m-2 min-w-0 flex-1 rounded-md p-2 text-left transition-colors hover:bg-slate-50/70',
                    )}
                    onClick={onSelectLive}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">Live editing</p>
                        <p className="mt-0.5 text-xs text-slate-500">Current editable draft</p>
                      </div>
                    </div>
                  </button>
                </li>
                {liveGroupVersions.map(renderVersionHistoryItem)}
              </ol>
            </section>

            {versions.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">No recorded versions yet.</div>
            ) : (
              recordedGroups.map((group) => (
                <section key={group.label} className="mb-5 last:mb-0">
                  <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    <Clock3 className="h-3 w-3" />
                    {group.label}
                  </div>
                  <ol className="relative">
                    {group.versions.map(renderVersionHistoryItem)}
                  </ol>
                </section>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
